import { icons } from '@/components/Base/Lucide';
import { templateTypesForAgent } from '@/utils/templateAgents';
import {
  canReachAdminPage,
  checkAdmin,
  checkBeacon,
  checkKeepme,
  checkSuperOwner,
  checkUserKeepme,
} from '@/utils/checkAccess';
import {
  getReachableSurfaces,
  hasPermission,
  isOnCustomRole,
  isPermissionRegistryReady,
} from '@/utils/permissionRegistry';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RESET_STORE } from './resetStore';
import { RootState } from './store';
import {
  getPersistedWorkspaceContext,
  persistWorkspaceContext,
  WorkspaceContext,
} from '@/utils/workSpaceContext';

export interface Menu {
  icon: keyof typeof icons;
  title: string;
  badge?: number;
  pathname?: string;
  subMenu?: Menu[];
  ignore?: boolean;
}

export interface SideMenuState {
  menu: Array<Menu | string>;
  allowReEngage: boolean;
  clientId: string | null;
  settingsView: boolean;
  googleAnalytics: boolean;
  activeAgent: string | null;
  workspaceContext: WorkspaceContext;
}

/**
 * Show a menu entry if its permission is held.
 *
 * Falls back to `true` until /roles/me resolves. The menu is built from a
 * non-reactive registry, so failing closed here would blank the sidebar on
 * first paint for every user; PrivateRoute rebuilds it once permissions land.
 */
export const showIfPermitted = (permission: string): boolean =>
  !isPermissionRegistryReady() || hasPermission(permission);

/**
 * Per-screen icons, matching the built-in menu where the same page appears
 * there, so a custom role's nav looks like everyone else's rather than a
 * column of identical glyphs.
 */
const SURFACE_ICONS: Record<string, keyof typeof icons> = {
  pulse: 'MessageSquare',
  overview: 'GaugeCircle',
  improve: 'Shield',
  performance: 'Timer',
  learnings: 'GraduationCap',
  benchmark: 'BarChart2',
  leads: 'UserPlus',
  conversations: 'MessagesSquare',
  queryFeedback: 'MessageCircle',
  agents: 'Layers',
  automations: 'Zap',
  templates: 'FileText',
  reengage: 'Repeat2',
  memberPayments: 'CreditCard',
  hubspot: 'Ticket',
  releaseNotes: 'Megaphone',
  configurations: 'Command',
  locations: 'MapPin',
  integrations: 'Package',
  team: 'Users',
  roles: 'KeyRound',
  billing: 'PercentSquare',
};

/**
 * Screens that Overview already drills into, so granting Overview makes a
 * separate entry redundant. Verified in pages/Nova: onLeadsClick and
 * onConversationClick navigate to /leads and /conversations, and
 * ClientAwareRouter prefixes the client id.
 *
 * Only the nav is trimmed. The surface list itself is untouched, because it
 * also resolves the landing page and the redirect for a denied route.
 */
const REACHED_VIA_OVERVIEW = ['leads', 'conversations'];

/**
 * Nav for a custom role, built from the screens the server says it can open.
 *
 * Built-in roles keep the hand-written menu below: their nav is a product
 * decision, not a function of their permissions. A custom role has no such
 * decision behind it, so anything it holds and nothing it does not.
 */
const surfaceMenu = (clientId?: string | null): SideMenuState['menu'] => {
  const p = (path: string) => (clientId ? `/${clientId}${path}` : path);
  const reachable = getReachableSurfaces();
  const hasOverview = reachable.some((s) => s.id === 'overview');

  const out: SideMenuState['menu'] = [];
  let section = '';
  for (const surface of reachable) {
    if (hasOverview && REACHED_VIA_OVERVIEW.includes(surface.id)) continue;
    if (surface.section !== section) {
      section = surface.section;
      out.push(section.toUpperCase());
    }
    out.push({
      icon: SURFACE_ICONS[surface.id] ?? ('Circle' as keyof typeof icons),
      pathname: p(surface.path.split('?')[0]),
      title: surface.label,
    });
  }
  return out;
};

const getMenuItems = (
  clientId?: string | null,
  allowReEngage?: boolean,
  googleAnalytics?: boolean,
  activeAgent?: string | null
): SideMenuState['menu'] => {
  // A custom role's nav comes from its permissions; built-in roles keep theirs.
  if (isPermissionRegistryReady() && isOnCustomRole()) {
    return surfaceMenu(clientId);
  }

  const isSuperOwner = checkSuperOwner();
  const isClient = !!clientId;
  const isAdmin = checkAdmin();
  const isUserKeepMe = checkUserKeepme();
  const isKeepme = checkKeepme();
  const isBeacon = checkBeacon();
  const readOnlyUser = isKeepme || isUserKeepMe;
  const p = (path: string) => (clientId ? `/${clientId}${path}` : path);

  const showGoogleAnalytics = !!googleAnalytics;

  if ((isSuperOwner || readOnlyUser) && !isClient) {
    return [
      'ORGANIZATION',
      {
        icon: 'Building' as keyof typeof icons,
        pathname: '/clients',
        title: 'Clients',
      },
    ];
  }

  return [
    'INSIGHTS',
    ...(showIfPermitted('insights:read')
      ? [
          {
            icon: 'GaugeCircle' as keyof typeof icons,
            pathname: p(`/${activeAgent}`),
            title: 'Overview',
          },
        ]
      : []),

    ...(canReachAdminPage('insights:analytics')
      ? [
          {
            icon: 'Shield' as keyof typeof icons,
            pathname: p(`/improvement`),
            title: 'Improve',
          },

          {
            icon: 'Timer' as keyof typeof icons,
            pathname: p(`/performance`),
            title: 'Performance',
          },

          {
            icon: 'GraduationCap' as keyof typeof icons,
            pathname: p(`/learnings`),
            title: 'Learnings',
          },

          {
            icon: 'BarChart2' as keyof typeof icons,
            pathname: p(`/benchmark`),
            title: 'Benchmark',
          },
        ]
      : []),

    'MANAGE',
    // Per entry, not on isAdmin: each route now needs its own permission, so
    // a link the user cannot open must not be shown.
    ...(isAdmin
      ? [
          ...(showIfPermitted('bots:read')
            ? [
                {
                  icon: 'Layers' as keyof typeof icons,
                  pathname: p('/agentTraining'),
                  title: 'Agent Training',
                },
              ]
            : []),
          ...(showIfPermitted('rules:read')
            ? [
                {
                  icon: 'Zap' as keyof typeof icons,
                  pathname: p('/automation'),
                  title: 'Automations',
                },
              ]
            : []),
          // Hidden for agents that have no template types (Clarion, Beacon).
          ...(showIfPermitted('templates:read') &&
          (!activeAgent || templateTypesForAgent(activeAgent).length)
            ? [
                {
                  icon: 'FileText' as keyof typeof icons,
                  pathname: p('/templates'),
                  title: 'Templates',
                },
              ]
            : []),
          ...(allowReEngage && showIfPermitted('reengage:read')
            ? [
                {
                  icon: 'Repeat2' as keyof typeof icons,
                  pathname: p('/campaign'),
                  title: 'Re-Engage Campaign',
                },
              ]
            : []),

          ...(showGoogleAnalytics
            ? [
                {
                  icon: 'LineChart' as keyof typeof icons,
                  pathname: '/google-analytics',
                  title: 'Google Analytics',
                },
              ]
            : []),
        ]
      : []),
  ];
};

const initialState: SideMenuState = {
  menu: [],
  allowReEngage: false,
  clientId: null,
  settingsView: false,
  googleAnalytics: false,
  activeAgent: null,
  workspaceContext: getPersistedWorkspaceContext(),
};

export const sideMenuSlice = createSlice({
  name: 'sideMenu',
  initialState,
  reducers: {
    updateMenu: (state, action: PayloadAction<string | null>) => {
      state.clientId = action.payload;
      state.menu = getMenuItems(
        action.payload,
        state.allowReEngage,
        state.googleAnalytics,
        state.activeAgent,
      );
    },
    setAllowReEngage: (state, action: PayloadAction<boolean>) => {
      state.allowReEngage = action.payload;
      state.menu = getMenuItems(
        state.clientId,
        action.payload,
        state.googleAnalytics,
        state.activeAgent,
      );
    },
    setSettingsView: (state, action: PayloadAction<boolean>) => {
      state.settingsView = action.payload;
    },
    setGoogleAnalytics: (state, action: PayloadAction<boolean>) => {
      state.googleAnalytics = action.payload;
      state.menu = getMenuItems(
        state.clientId,
        state.allowReEngage,
        action.payload,
        state.activeAgent,
      );
    },
    setCurrentAgent: (state, action: PayloadAction<string>) => {
      state.activeAgent = action.payload;
      state.menu = getMenuItems(
        state.clientId,
        state.allowReEngage,
        state.googleAnalytics,
        action.payload
      );
    },
    toggleSettingsView: (state) => {
      state.settingsView = !state.settingsView;
    },
    // Single dispatch point for switching between Intelligence/chat mode
    // and a specific agent's mode. Keeps workspaceContext, activeAgent,
    // and the generated menu all in sync from one action instead of
    // requiring callers to dispatch setCurrentAgent separately (which
    // previously let Redux state and localStorage drift apart).
    setWorkspaceContext: (state, action: PayloadAction<WorkspaceContext>) => {
      state.workspaceContext = action.payload;
      persistWorkspaceContext(action.payload);

      const agentId =
        action.payload.kind === 'agent' ? action.payload.agentId : null;
      state.activeAgent = agentId;
      state.menu = getMenuItems(
        state.clientId,
        state.allowReEngage,
        state.googleAnalytics,
        agentId
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(RESET_STORE, () => ({
      ...initialState,
      workspaceContext: getPersistedWorkspaceContext(),
    }));
  },
});

export const selectSideMenu = (state: RootState) => state.sideMenu.menu;
export const selectSettingsView = (state: RootState) =>
  state.sideMenu.settingsView;
export const selectWorkspaceContext = (state: RootState) =>
  state.sideMenu.workspaceContext;
export const selectActiveAgent = (state: RootState) =>
  state.sideMenu.activeAgent;

export const {
  updateMenu,
  setAllowReEngage,
  setSettingsView,
  toggleSettingsView,
  setGoogleAnalytics,
  setCurrentAgent,
  setWorkspaceContext,
} = sideMenuSlice.actions;

export default sideMenuSlice.reducer;
